/**
 * Propuestas de intercambio directas entre usuarios.
 */
import { api } from "./client";
import type { MatchUser } from "./matches";

export type TradeStatus = "pending" | "accepted" | "rejected" | "cancelled" | "completed" | "countered" | "disputed";
export type TradeDirection = "give" | "receive";

export interface TradeItemIn {
  sticker_id: number;
  direction: TradeDirection;
  quantity?: number;
}

export interface CounterOfferPayload {
  message?: string;
  items: TradeItemIn[];
  cash_delta?: number;
}

export interface TradeItemOut {
  id: number;
  sticker_id: number;
  sticker_num: string;
  sticker_name: string;
  direction: TradeDirection;
  quantity: number;
}

export interface TradeProposalCreatePayload {
  to_user_id: number;
  message?: string;
  items: TradeItemIn[];
  cash_delta?: number;
}

export interface TradeProposal {
  id: number;
  from_user: MatchUser;
  to_user: MatchUser;
  status: TradeStatus;
  message: string | null;
  items: TradeItemOut[];
  cash_delta: number;
  counter_of_id: number | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  give_count: number;
  receive_count: number;
}

export interface TradeSummary {
  incoming_pending: number;
  outgoing_pending: number;
}

export async function createProposal(payload: TradeProposalCreatePayload): Promise<TradeProposal> {
  return api<TradeProposal>("/trade-proposals", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchMyProposals(direction: "incoming" | "outgoing" | "all" = "all", status?: TradeStatus): Promise<TradeProposal[]> {
  const params = new URLSearchParams({ direction });
  if (status) params.set("status", status);
  return api<TradeProposal[]>(`/trade-proposals/me?${params}`);
}

export async function fetchProposalSummary(): Promise<TradeSummary> {
  return api<TradeSummary>("/trade-proposals/me/summary");
}

export async function acceptProposal(id: number): Promise<TradeProposal> {
  return api<TradeProposal>(`/trade-proposals/${id}/accept`, { method: "POST" });
}

export async function rejectProposal(id: number): Promise<TradeProposal> {
  return api<TradeProposal>(`/trade-proposals/${id}/reject`, { method: "POST" });
}

export async function cancelProposal(id: number): Promise<TradeProposal> {
  return api<TradeProposal>(`/trade-proposals/${id}/cancel`, { method: "POST" });
}

export async function counterProposal(id: number, payload: CounterOfferPayload): Promise<TradeProposal> {
  return api<TradeProposal>(`/trade-proposals/${id}/counter`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchActiveProposalWith(peerId: number): Promise<TradeProposal | null> {
  return api<TradeProposal | null>(`/trade-proposals/with/${peerId}/active`);
}

export async function completeProposal(id: number, applyInventory = false): Promise<TradeProposal> {
  const qs = applyInventory ? "?apply_inventory=true" : "";
  return api<TradeProposal>(`/trade-proposals/${id}/complete${qs}`, { method: "POST" });
}
